import { useId, useObjectRef, useSlotId } from '@react-aria/utils';
import type { HTMLAttributes } from 'react';
import { forwardRef, useContext } from 'react';
import type { ToastProps } from 'react-aria-components';
import {
  UNSTABLE_Toast as AriaToast,
  UNSTABLE_ToastContent as AriaToastContent,
  UNSTABLE_ToastStateContext as AriaToastStateContext,
} from 'react-aria-components';

import { easing } from '@infinex/ui-constants';
import { CloseIcon } from '@infinex/ui-icons';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn } from '@infinex/ui-styles';
import { joinIds } from '@infinex/ui-utils/a11y';

import { ToasterContext } from './context';
import type { ToastOptions, ToastValue, ToastVariant } from './ToastQueue';

import { Badge } from '../badge';
import { Button } from '../button';
import { CenterBaseline, Text } from '../content';

export const MIN_HEIGHT = 80;
const springConfig = { mass: 0.8, damping: 22, stiffness: 300 };
export const springTransition = { type: 'spring' as const, ...springConfig };
export const timeTransition = { duration: 0.3, ease: easing.easeOutSmooth };

type ToastPrimitiveProps = Pick<ToastProps<ToastValue>, 'toast'> &
  UnsafeStyleProps & {
    reduceMotion: boolean;
  };

/**
 * @private Internal component used to render a toast notification.
 */
export const ToastPrimitive = forwardRef<HTMLDivElement, ToastPrimitiveProps>(
  function ToastPrimitive(props, forwardedRef) {
    const { className, style, toast } = props;
    const { actionElement, content, title, variant } = toast.content;

    const ref = useObjectRef(forwardedRef);
    const state = useContext(AriaToastStateContext)!;
    const { isExpanded, placement } = useContext(ToasterContext);
    const index = state.visibleToasts.indexOf(toast);
    const isFront = index <= 0; // -1 while exiting
    const isBehind = !isExpanded && !isFront;

    const hasBadge = isBadgeVariant(variant);
    const badgeId = useSlotId([hasBadge]);
    const titleId = useId();

    const sharedClasses =
      'bg-surface border-tertiary rounded-medium border shadow-md';
    const sharedStyle = { width: '100%', ...style };

    // when collapsed, an empty div is used for "background" toasts
    if (isBehind) {
      // do not auto-dismiss while hidden
      toast.timer?.pause();

      return (
        <div
          role="presentation"
          className={sharedClasses}
          style={{
            height: MIN_HEIGHT,
            left: 0,
            position: 'absolute',
            [placement === 'top' ? 'bottom' : 'top']: 0,
            // Only 3 toasts are visible in the stack at once, but all toasts are in
            // the DOM. This lets us smoothly animate from where the element would be
            // in the collapsed stack to its final position in the expanded list.
            opacity: index >= 3 ? 0 : 1,
            ...sharedStyle,
          }}
        />
      );
    }

    return (
      <AriaToast
        aria-labelledby={joinIds(badgeId, titleId)}
        ref={ref}
        toast={toast}
        className={cn(
          sharedClasses,
          'group relative flex items-baseline',
          'focus-visible:outline-focusRing outline-hidden',
          className
        )}
        style={sharedStyle}
      >
        <AriaToastContent className="grid flex-1 gap-y-2 py-4 pl-5">
          <div className="flex items-baseline justify-between gap-x-2">
            <Text
              slot="title"
              id={titleId}
              className="text-emphasis text-sm font-medium"
            >
              {title}
            </Text>

            {hasBadge && (
              <CenterBaseline>
                <VariantBadge variant={variant} id={badgeId} />
              </CenterBaseline>
            )}
          </div>

          <div className="text-default flex items-center gap-x-2 text-sm">
            <Text slot="description" className="flex-1">
              {content}
            </Text>

            {typeof actionElement === 'function'
              ? actionElement(toast.key)
              : actionElement}
          </div>
        </AriaToastContent>

        <CenterBaseline className="px-2">
          <Button
            className="text-default rounded-full"
            prominence="low"
            slot="close"
            size="small"
          >
            <CloseIcon />
          </Button>
        </CenterBaseline>
      </AriaToast>
    );
  }
);

// Styled components
// -----------------------------------------------------------------------------

const BADGE_TEXT: Record<NonNeutralVariant, string> = {
  onchain: 'Onchain',
  error: 'Error',
};

const BADGE_TONE = {
  onchain: 'brand',
  error: 'critical',
} as const;

type VariantBadgeProps = {
  variant: NonNeutralVariant;
} & HTMLAttributes<HTMLDivElement>;

function VariantBadge(props: VariantBadgeProps) {
  const { variant, ...domProps } = props;

  return (
    <Badge
      {...domProps}
      tone={BADGE_TONE[variant]}
      aria-hidden // referenced by id, remove from a11y tree
    >
      {BADGE_TEXT[variant]}
    </Badge>
  );
}

type NonNeutralVariant = Exclude<ToastVariant, 'neutral'>;

function isBadgeVariant(variant: ToastVariant): variant is NonNeutralVariant {
  return variant !== 'neutral';
}

/**
 * @private Spoof component to get Storybook’s `argTypes` working. Match
 * prop types with the options for `ToastQueue.add()`.
 *
 * NOTE: Must be exported, doesn’t work inline from the stories file.
 */
export function StorybookArgTypesHack(props: ToastOptions) {
  return <div>{props.content}</div>;
}
